import AIState, { useAIState } from "@/components/AIState";
import OperationalState, { operationallyAllowed, collectionAllowed, validCollectionLimits, collectionAuthorization } from "@/components/OperationalState";
import { useRead, ReadState, isCreators } from "@/components/LoadingStates";
import LiveDemoBadge, { isLiveCreator } from "@/components/LiveDemoBadge";
import MediaImage from "@/components/MediaImage";
import { useState } from "react";
import { formatError } from "@/lib/api";
import { storedCollection, submitCollection, refreshCollection, continueCollection } from "@/lib/collectionOperations";
import CollectionOperationState from "@/components/CollectionOperationState";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PlatformBadge from "@/components/PlatformBadge";
import { Plug, RefreshCw, CheckCircle2, XCircle, Clock, Sparkles } from "lucide-react";
import { formatNumber, timeAgo, timeUntil, classNames } from "@/lib/format";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

// Latest last_synced_at among the Live profiles of this platform received from /ingest/status: a recorded
// collection, not a health check. R5-B: the server list is capped and has no total, so the result only
// covers the received profiles.
function lastRecordedCollection(creators, platform) {
  let latest = null, received = 0;
  for (const c of creators || []) {
    if (c?.platform !== platform) continue;
    received += 1;
    const t = typeof c.last_synced_at === "string" ? Date.parse(c.last_synced_at) : NaN;
    if (Number.isFinite(t) && (!latest || t > latest.t)) latest = { t, value: c.last_synced_at };
  }
  return { received, latest: latest?.value || null };
}

const AUTHORIZATION = { allowed: "oui", denied: "non", unknown: "inconnue" };

function lastSyncText({ received, latest }) {
  const scope = received === 1 ? "le profil Live reçu" : `les ${received} profils Live reçus`;
  if (latest) return `Dernière collecte enregistrée parmi ${scope} pour cette plateforme : ${timeAgo(latest)}. La liste renvoyée par le serveur est plafonnée et sans total : ni exhaustivité ni état actuel du fournisseur garantis.`;
  if (received) return `Aucun horodatage de collecte exploitable parmi ${scope} pour cette plateforme. Cela n’établit pas qu’aucune collecte n’a eu lieu.`;
  return "Aucun profil Live de cette plateforme dans la liste reçue : aucune date de collecte affichable. Cela n’établit pas qu’aucune collecte n’a eu lieu.";
}

export default function Sources() {
  const ai = useAIState("video_manual");
  const { canEdit, user } = useAuth();
  const [operationState, setOperationState] = useState(() => {
    try {
      for (const action of ["sync-all", "sync-job"]) {
        const operation = storedCollection(user, action);
        if (operation?.state === "stopped") return { action, operation };
      }
    } catch {} // Existing storage failures still refuse submission in the helper.
    return null;
  });
  const statusRead = useRead("/ingest/status", (d) => d && Array.isArray(d.providers) && d.providers.every((p) => p && typeof p.platform === "string" && typeof p.connected === "boolean") && isCreators(d.live_creators) && Array.isArray(d.jobs) && d.jobs.every((j) => j && typeof j.name === "string") && (!d.auto_analysis || Array.isArray(d.auto_analysis.recent)));
  const status = statusRead.data;
  const collectionEnabled = operationallyAllowed(status?.operations, "manual_collection") && collectionAllowed(status?.operations);
  const scheduledEnabled = operationallyAllowed(status?.operations, "scheduled_sync") && collectionAllowed(status?.operations);
  const batchAllowed = !status?.operations?.private || (validCollectionLimits(status.operations.collection_limits)
    && status.operations.collection_limits.config.batch_max > 0
    && status.live_creators.length <= status.operations.collection_limits.config.batch_max);
  const [busy, setBusy] = useState(null);

  const load = () => statusRead.reload().catch(() => {});

  const sync = async (id) => {
    if (!canEdit || !collectionEnabled || !collectionAllowed(status?.operations, status.live_creators.find((c) => c.id === id)?.platform) || busy || statusRead.status !== "success") return;
    setBusy(id);
    try {
      const data = await submitCollection(user, `sync:${id}`, `/ingest/creators/${id}/sync`);
      setOperationState({ action: `sync:${id}`, operation: data.operation });
      if (data.operation.state === "completed") toast.success(`Synced ${data.videos_ingested} videos`);
      load();
    } catch (err) {
      try { setOperationState({ action: `sync:${id}`, operation: storedCollection(user, `sync:${id}`), error: formatError(err) }); } catch {}
      toast.error(formatError(err));
    } finally {
      setBusy(null);
    }
  };

  const syncAll = async (newBatch = false) => {
    if (!canEdit || !collectionEnabled || !batchAllowed || busy || statusRead.status !== "success") return;
    setBusy("all");
    try {
      const data = await submitCollection(user, "sync-all", "/ingest/sync-all", {}, { newBatch });
      setOperationState({ action: "sync-all", operation: data.operation });
      if (data.operation.state === "completed") {
        if (data.ok) toast.success(`Synced ${data.synced}/${data.results.length} creators`);
        else toast.error(`Synchronisation partielle : ${data.synced}/${data.results.length}. Consultez les échecs.`);
      }
      load();
    } catch (err) {
      try { setOperationState({ action: "sync-all", operation: storedCollection(user, "sync-all"), error: formatError(err) }); } catch {}
      toast.error(formatError(err));
    } finally {
      setBusy(null);
    }
  };

  const runJob = async (newBatch = false) => {
    if (!canEdit || !scheduledEnabled || !batchAllowed || busy || statusRead.status !== "success") return;
    setBusy("job");
    try {
      const data = await submitCollection(user, "sync-job", "/jobs/sync_live/run", {}, { newBatch });
      setOperationState({ action: "sync-job", operation: data.operation });
      if (data.ok) toast.success(`Auto-resync ran: ${data.result.synced}/${data.result.total} creators`);
      else toast.error(data.error || "Job failed");
      load();
    } catch (err) {
      try { setOperationState({ action: "sync-job", operation: storedCollection(user, "sync-job"), error: formatError(err) }); } catch {}
      toast.error(formatError(err));
    } finally {
      setBusy(null);
    }
  };

  const continueBatch = async () => {
    const enabled = operationState?.action === "sync-job" ? scheduledEnabled : collectionEnabled;
    if (!canEdit || !enabled || !batchAllowed || busy || statusRead.status !== "success") return;
    setBusy("continue");
    try {
      const data = await continueCollection(user, operationState.action);
      setOperationState({ action: operationState.action, operation: data.operation });
      load();
    } catch (error) { setOperationState((value) => ({ ...value, error: formatError(error) })); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="sources-page">
      <div>
        <div className="flex items-center gap-2">
          <Plug className="h-6 w-6 text-primary" />
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Integrations</h1>
        </div>
        <p className="text-muted-foreground text-sm mt-1">
          Consultez la configuration des fournisseurs et synchronisez vos créateurs suivis.
        </p>
      </div>

      <p className="text-sm"><Link to="/creators" className="text-primary hover:underline" data-testid="integrations-creators-link">Add creators from the Creators page →</Link></p>
      {!canEdit && <p className="text-xs text-muted-foreground" data-testid="sources-read-only-hint">Un Owner ou Editor peut ajouter ou synchroniser des créateurs dans ce workspace.</p>}

      <ReadState read={statusRead} scope="sources">
      <CollectionOperationState {...operationState} busy={!!busy} scope="sources"
        canContinue={canEdit && batchAllowed && statusRead.status === "success" && (operationState?.action === "sync-job" ? scheduledEnabled : collectionEnabled)}
        onContinue={continueBatch} onNewBatch={() => operationState?.action === "sync-job" ? runJob(true) : syncAll(true)} onRefresh={async () => {
        setBusy("status");
        try { const data = await refreshCollection(user, operationState.action); setOperationState({ action: operationState.action, operation: data.operation }); load(); }
        catch (err) { setOperationState((value) => ({ ...value, error: formatError(err) })); }
        finally { setBusy(null); }
      }} />
      <Button variant="outline" onClick={load} disabled={statusRead.isRefreshing} data-testid="collection-limits-refresh">Actualiser les allocations (lecture seule)</Button>
      <AIState read={ai.read} scope="integrations" />
      <OperationalState state={status?.operations} capabilities={["manual_collection", "scheduled_sync", "manual_ai", "automatic_analysis"]} scope="integrations" />
      {!batchAllowed && <p role="status" data-testid="collection-batch-limit-hint">Lot désactivé : le maximum de profils est nul, invérifiable ou inférieur au nombre de profils suivis.</p>}
      <p className="text-xs text-muted-foreground" data-testid="provider-status-scope">« Paramètres présents » signifie seulement que les variables requises sont définies côté serveur. Cela ne prouve ni la validité du token, ni la disponibilité du fournisseur : aucun appel fournisseur n’est fait pour afficher cet état.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="provider-grid">
        {(status?.providers || []).map((p) => {
          const authorization = collectionAuthorization(status?.operations, p.platform);
          const lastSynced = lastRecordedCollection(status?.live_creators, p.platform);
          return (
          <Card key={p.platform} className="p-5" data-testid={`provider-card-${p.platform}`}>
            <div className="flex items-center justify-between">
              <PlatformBadge platform={p.platform} />
              <span
                className={classNames(
                  "inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5",
                  p.connected ? "bg-[hsl(var(--emerald)/0.12)] text-[hsl(var(--emerald))]" : "bg-muted text-muted-foreground"
                )}
                data-testid={`provider-status-${p.platform}`}
              >
                {p.connected ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                {p.connected ? "Paramètres présents" : "Paramètres absents"}
              </span>
            </div>
            <div className="mt-3 font-semibold">{p.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{p.via}</div>
            <ul className="mt-3 space-y-0.5 text-xs text-muted-foreground" data-testid={`provider-facts-${p.platform}`}>
              <li data-testid={`provider-config-${p.platform}`}>Configuration serveur : {p.connected ? "variables requises présentes (validité non vérifiée)" : "variables requises absentes"}</li>
              <li data-testid={`provider-allowed-${p.platform}`} data-state={authorization.state}>Collecte manuelle autorisée (politique et allocations) : {AUTHORIZATION[authorization.state]} — {authorization.reason}</li>
              <li data-testid={`provider-last-sync-${p.platform}`}>{lastSyncText(lastSynced)}</li>
            </ul>
            {!p.connected && (
              <div className="mt-3 rounded-md bg-muted/50 px-3 py-2 font-mono text-[11px] text-muted-foreground">
                set <span className="text-foreground">{p.env_var}</span> in backend/.env
              </div>
            )}
          </Card>
          );
        })}
      </div>

      <Card className="p-5" data-testid="live-creators-card">
        {status?.jobs?.filter((j) => j.name === "sync_live").map((j) => (
          <div key={j.name} className="mb-4 flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2 text-xs" data-testid="auto-sync-status">
            <Clock className="h-4 w-4 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-medium">Auto-resync every {j.interval_hours}h</span>
              <span className="text-muted-foreground"> · {scheduledEnabled ? `next run ${timeUntil(j.next_run_at)}` : "désactivée par la politique privée"} · last run {j.last_run_at ? timeAgo(j.last_run_at) : "never"}</span>
              {j.last_status === "error" && <span className="text-[hsl(var(--rose))] ml-1">(last run failed)</span>}
              {j.running && <span className="text-primary ml-1">· running now</span>}
            </div>
            {canEdit && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => runJob()} disabled={!canEdit || !scheduledEnabled || !batchAllowed || busy === "job" || j.running} data-testid="run-sync-job-button">
              <RefreshCw className={classNames("h-3.5 w-3.5 mr-1", (busy === "job" || j.running) && "animate-spin")} /> Run now
            </Button>}
          </div>
        ))}
        {status?.auto_analysis && (
          <div className="mb-4 flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2 text-xs" data-testid="auto-analysis-status">
            <Sparkles className="h-4 w-4 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-medium">Auto-analyze new breakouts</span>
              <span className="text-muted-foreground"> · {operationallyAllowed(status?.operations, "automatic_analysis") ? "Active" : "Désactivée par la politique privée"} · Newly detected live outliers (max {status.auto_analysis.max_per_sync}/sync) · {status.auto_analysis.analyzed} analyzed{status.auto_analysis.failed > 0 && `, ${status.auto_analysis.failed} failed`}</span>
            </div>
            {status.auto_analysis.recent[0] && (
              <Link to={`/videos/${status.auto_analysis.recent[0].video_id}`} className="text-primary hover:underline truncate max-w-[220px]" data-testid="auto-analysis-latest">
                latest: {status.auto_analysis.recent[0].title}
              </Link>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h3 className="font-display font-bold text-lg">Live creators</h3>
            <p className="text-xs text-muted-foreground">
              {status ? `${status.live_creators.length} live · ${status.live_videos} videos · ${status.mock_creators} demo creators` : "Loading…"}
            </p>
          </div>
          {canEdit && <Button variant="outline" size="sm" onClick={() => syncAll()} disabled={!canEdit || !collectionEnabled || !batchAllowed || !status?.live_creators.length || busy === "all"} data-testid="sync-all-button">
            <RefreshCw className={classNames("h-4 w-4 mr-1", busy === "all" && "animate-spin")} /> Sync all
          </Button>}
        </div>
        {status && status.live_creators.length === 0 ? (
          <div data-testid="sources-empty"><div className="text-sm text-muted-foreground py-6 text-center" data-testid="live-creators-empty">
            Aucun créateur Live n’est suivi. Ajoutez un créateur depuis Creators.
            <Link to="/creators" data-testid="sources-empty-cta" className="block text-primary mt-2">Consulter Creators</Link>
          </div></div>
        ) : (
          <div className="divide-y divide-border">
            {(status?.live_creators || []).map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-3" data-testid={`live-creator-row-${c.id}`}>
                <MediaImage src={c.avatar} alt="" name={c.name} kind="avatar" demo={!isLiveCreator(c)} className="h-9 w-9 rounded-full object-cover bg-muted" />
                <div className="flex-1 min-w-0">
                  <Link to={`/creators/${c.id}`} className="text-sm font-medium truncate hover:underline block">{c.name} <LiveDemoBadge creator={c} /></Link>
                  <div className="text-xs text-muted-foreground truncate">{c.handle} · {c.niche}</div>
                </div>
                <PlatformBadge platform={c.platform} size="xs" />
                <div className="hidden sm:block text-right w-24">
                  <div className="font-mono text-sm font-bold">{formatNumber(c.followers)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Followers</div>
                </div>
                <div className="hidden sm:block text-right w-20">
                  <div className="font-mono text-sm font-bold">{c.video_count}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Videos</div>
                </div>
                <div className="hidden sm:block text-right w-20">
                  <div className="font-mono text-sm font-bold text-[hsl(var(--emerald))]">{c.outliers}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Outliers</div>
                </div>
                <div className="hidden md:block text-xs text-muted-foreground w-28 text-right">synced {timeAgo(c.last_synced_at)}</div>
                {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => sync(c.id)} disabled={!canEdit || !collectionEnabled || !collectionAllowed(status?.operations, c.platform) || busy === c.id} data-testid={`sync-creator-${c.id}`} aria-label="Sync">
                  <RefreshCw className={classNames("h-4 w-4", busy === c.id && "animate-spin")} />
                </Button>}
              </div>
            ))}
          </div>
        )}
      </Card>
      </ReadState>
    </div>
  );
}
